import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Divider,
  IconButton,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import RoundedButton from 'components/Shared/RoundedButton';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      boxShadow: '0 24px 56px rgba(0, 0, 0, 0.15), 0 12px 32px rgba(0, 0, 0, 0.1)',
      maxWidth: 520,
      width: '100%',
      margin: theme.spacing(2),
      overflow: 'hidden',
    },
  },
  header: {
    position: 'relative',
    padding: theme.spacing(3, 3, 0),
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: theme.palette.grey[600],
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: theme.colors?.blue2 || '#122056',
    marginBottom: theme.spacing(1),
    paddingRight: theme.spacing(5),
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.4,
  },
  content: {
    padding: theme.spacing(2, 3, 0),
    '&:first-child': {
      paddingTop: theme.spacing(2),
    },
  },
  requirementsList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  requirement: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1.5),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  requirementIcon: {
    fontSize: 16,
    color: theme.colors?.lightBlue || '#5B65DC',
    marginRight: theme.spacing(1),
    marginTop: 2,
    flexShrink: 0,
  },
  checkboxContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: 'rgba(0, 168, 255, 0.04)',
    borderRadius: 12,
    border: '1px solid rgba(0, 168, 255, 0.12)',
  },
  checkboxLabel: {
    margin: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette.text.primary,
      lineHeight: 1.4,
    },
    '& .MuiCheckbox-root': {
      padding: theme.spacing(0.5),
      marginRight: theme.spacing(1),
      color: theme.colors?.lightBlue || '#5B65DC',
      '&.Mui-checked': {
        color: theme.colors?.lightBlue || '#5B65DC',
      },
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
  },
  actions: {
    padding: theme.spacing(2, 3, 3),
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  cancelButton: {
    textTransform: 'none',
    borderRadius: 10,
    padding: theme.spacing(1, 3),
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      borderColor: theme.palette.text.secondary,
    },
  },
  submitButton: {
    textTransform: 'none',
    borderRadius: 10,
    padding: theme.spacing(1, 3),
    fontSize: '0.875rem',
    fontWeight: 600,
    background: `linear-gradient(135deg, ${theme.colors?.lightBlue || '#5B65DC'} 0%, ${theme.colors?.blue || '#0088CC'} 100%)`,
    boxShadow: '0 4px 12px rgba(0, 168, 255, 0.25)',
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.colors?.blue || '#0088CC'} 0%, #0066AA 100%)`,
      boxShadow: '0 6px 20px rgba(0, 168, 255, 0.35)',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      background: theme.palette.grey[300],
      color: theme.palette.grey[500],
      boxShadow: 'none',
      transform: 'none',
    },
    transition: 'all 0.2s ease',
  },
}));

const CreateOrderDialog = ({ open, onClose, onSubmit }) => {
  const { en: { dialogs, btn } } = useSelector((state) => state.i18n);
  const classes = useStyles();
  const [isChecked, setChecked] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="create-title"
      className={classes.dialog}
    >
      <Box className={classes.header}>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>

        <Typography className={classes.title}>
          {dialogs.prescriberDeclaration}
        </Typography>

        <Typography className={classes.subtitle}>
          {dialogs.bySavingThisPrescription}
        </Typography>
      </Box>

      <DialogContent className={classes.content}>
        <ul className={classes.requirementsList}>
          <li className={classes.requirement}>
            <span>{dialogs.requirementA}</span>
          </li>
          <li className={classes.requirement}>
            <span>{dialogs.requirementB}</span>
          </li>
          <li className={classes.requirement}>
            <span>{dialogs.requirementC}</span>
          </li>
        </ul>

        <Box className={classes.checkboxContainer}>
          <FormControlLabel
            className={classes.checkboxLabel}
            control={
              <Checkbox
                checked={isChecked}
                onChange={(e, checked) => {
                  setChecked(checked);
                }}
              />
            }
            label={dialogs.prescribingPhysician}
          />
        </Box>
      </DialogContent>

      <Divider className={classes.divider} />

      <DialogActions className={classes.actions}>
        <RoundedButton
          onClick={onClose}
          variant="contained"
          color="secondary"
          size="small"
        >
          {btn.cancel}
        </RoundedButton>
        <RoundedButton
          onClick={onSubmit}
          variant="contained"
          color="primary"
          size="small"
          className={classes.btn}
          disabled={!isChecked}
        >
          {btn.save}
        </RoundedButton>
      </DialogActions>
    </Dialog>
  );
};

CreateOrderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default CreateOrderDialog;
